import type { NavIdProps } from '@vkontakte/vkui';
import type { ReactNode } from 'react';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { InfoIcon } from '../../shared/icons';
import {
  Bullet,
  Card,
  CardHeading,
  CardList,
  ChipRow,
  Gap,
  Gutter,
  Muted,
  Notice,
  Prose,
  Screen,
  Section,
  SectionTitle,
  Tag,
  type BulletTone,
} from '../../shared/ui';

/**
 * Краткая выжимка правил анкеты.
 *
 * Экран нужен ровно для одного: игрок должен узнать про запреты ДО того, как
 * получит отказ. Отказ с комментарием «эльфы запрещены» после недели ожидания
 * в очереди — это провал приложения, а не работа модератора.
 *
 * Полные правила живут в сообществе; здесь только то, обо что чаще всего
 * спотыкаются при первой анкете.
 */

/** Расы, которые модератор завернёт сразу. Раса вводится свободным текстом. */
const FORBIDDEN_RACES = [
  t('more.rules.race.forbidden.gods'),
  t('more.rules.race.forbidden.demigods'),
  t('more.rules.race.forbidden.angels'),
  t('more.rules.race.forbidden.demons'),
  t('more.rules.race.forbidden.elves'),
  t('more.rules.race.forbidden.fairies'),
  t('more.rules.race.forbidden.fey'),
  t('more.rules.race.forbidden.dragons'),
  t('more.rules.race.forbidden.centaurs'),
  t('more.rules.race.forbidden.lynxWerewolves'),
];

export function RulesPanel({ id }: NavIdProps) {
  const back = useBack();

  return (
    <Screen id={id} title={t('more.rules.title')} back={back}>
      <Section>
        {/* Бирюза, а не малиновый: второй акцент на этом экране забирают
            запрещённые расы ниже. */}
        <SectionTitle>{t('more.rules.intro.title')}</SectionTitle>
        <Gutter>
          <Prose>{t('more.rules.intro.text')}</Prose>
        </Gutter>
      </Section>

      <Section>
        <SectionTitle accent="pink">{t('more.rules.race.title')}</SectionTitle>
        <Gutter>
          <ChipRow>
            {/* Метка, а не бейдж состояния: раса ничего не сообщает о состоянии
                вещи, она просто названа. И крупной метрикой — это содержимое
                раздела, а не подпись при названии внутри карточки. */}
            {FORBIDDEN_RACES.map((race) => (
              <Tag key={race} tone="error" size="l">
                {race}
              </Tag>
            ))}
          </ChipRow>
          <Gap step="s2h" />
          <Muted>{t('more.rules.race.hint')}</Muted>
        </Gutter>
      </Section>

      <Section>
        <SectionTitle>{t('common.section.abilities')}</SectionTitle>
        <CardList>
          <Rule tone="lilac" title={t('more.rules.ability.limit.title')}>
            {t('more.rules.ability.limit.text')}
          </Rule>
          <Rule tone="lilac" title={t('more.rules.ability.forbiddenSchools.title')}>
            {t('more.rules.ability.forbiddenSchools.text')}
          </Rule>
          <Rule tone="lilac" title={t('more.rules.ability.description.title')}>
            {t('more.rules.ability.description.text')}
          </Rule>
        </CardList>
      </Section>

      <Section>
        <SectionTitle>{t('more.rules.review.title')}</SectionTitle>
        <CardList>
          <Rule tone="cyan" title={t('more.rules.review.dorm.title')}>
            {t('more.rules.review.dorm.text')}
          </Rule>
          <Rule tone="cyan" title={t('more.rules.review.edit.title')}>
            {t('more.rules.review.edit.text')}
          </Rule>
          <Rule tone="cyan" title={t('more.rules.review.comment.title')}>
            {t('more.rules.review.comment.text')}
          </Rule>
          <Rule tone="cyan" title={t('more.rules.review.registry.title')}>
            {t('more.rules.review.registry.text')}
          </Rule>
        </CardList>
      </Section>

      {/* Своим разделом, а не довеском к последнему: шов от карточек выше
          держит раздел, и держит он его один на весь экран. В рамке и в тех
          же полях, что карточки: во всю ширину экрана плашка читалась
          подвалом страницы, а это приглашение спросить. */}
      <Section>
        <Gutter>
          <Notice
            tone="warning"
            framed
            icon={<InfoIcon tone="lilac" />}
            title={t('more.rules.doubts.title')}
          >
            {t('more.rules.doubts.text')}
          </Notice>
        </Gutter>
      </Section>
    </Screen>
  );
}

/**
 * Пункт правил: ромб-маркер слева, заголовок и текст справа.
 *
 * Цвет ромба и контура карточки задаёт БЛОК, а не пункт: лиловый у запретов,
 * бирюзовый у порядка проверки. Два одинаковых столбца карточек подряд
 * читались как один длинный список, и понять, где кончаются запреты,
 * приходилось по заголовку раздела.
 */
function Rule({ title, tone, children }: { title: string; tone: BulletTone; children: ReactNode }) {
  return (
    <Card tone={tone === 'cyan' ? 'published' : 'default'}>
      <Bullet tone={tone} shape="diamond">
        <CardHeading>{title}</CardHeading>
        <Gap step="s1h" />
        <Muted>{children}</Muted>
      </Bullet>
    </Card>
  );
}
